/* eslint-disable max-lines-per-function */
import { HttpErrorResponse } from '@angular/common/http';

import { MemoizedSelector, createSelectorFactory, defaultMemoize } from '@ngrx/store';

import { Location, LocationsScope } from '@recruitee/http/types';
import {
  ID,
  createEntityCollectionStore,
  getEntityCollectionActions,
  memoizeSelector,
  pipeState,
} from '@recruitee/ngrx';

import { LocationDataPayload, LocationsPayloadParams } from '../location.types';

const { actions, selectors, reducer, namespace } = createEntityCollectionStore<Location>()(
  'Locations',
  {
    customActions: adapter => {
      const defaultActions = getEntityCollectionActions(adapter, ({ id }) => id!);

      return {
        fetchPaginatedCollection: (
          state,
          payload: { scope: LocationsScope; params: LocationsPayloadParams },
        ) => defaultActions.fetchPaginatedCollection(state, payload),
        fetchBriefCollection: defaultActions.fetchCollection,
        fetchBriefCollectionSuccess: defaultActions.fetchCollectionSuccess,
        fetchBriefCollectionFailed: defaultActions.fetchCollectionFailed,
        createOnePessimistic: (state, payload: { data: LocationDataPayload }) =>
          adapter.markAsPending(state),
        updateOnePessimistic: (state, payload: { data: LocationDataPayload; id: ID }) =>
          adapter.markAsPending(state, { scope: null, entities: [payload.id] }),
        archive: (state, payload: { id: ID }) => adapter.markAsPending(state),
        archiveSuccess: (state, payload: { data: Location }) =>
          pipeState(
            state,
            next => adapter.removeOne(next, payload.data.id),
            next =>
              adapter.addOne(next, payload.data, {
                scope: LocationsScope.Archived,
              }),
            next => adapter.markAsLoaded(next),
          ),
        archiveFailed: defaultActions.updateOnePessimisticFailed,
        retrieve: (state, payload: { id: ID }) => adapter.markAsPending(state),
        retrieveSuccess: (state, payload: { data: Location }) =>
          pipeState(
            state,
            next => adapter.removeOne(next, payload.data.id),
            next =>
              adapter.addOne(next, payload.data, {
                scope: LocationsScope.Active,
              }),
            next => adapter.markAsLoaded(next),
          ),
        retrieveFailed: defaultActions.createOnePessimisticFailed,
        duplicate: (state, payload: { id: ID }) => adapter.markAsPending(state),
        duplicateSuccess: (state, payload: { data: Location }) =>
          pipeState(
            state,
            next =>
              adapter.addOne(next, payload.data, {
                scope: LocationsScope.Active,
                prepend: true,
              }),
            next => adapter.markAsLoaded(next),
          ),
        duplicateFailed: defaultActions.createOnePessimisticFailed,
        delete: defaultActions.removeOnePessimistic,
        deleteSuccess: defaultActions.removeOnePessimisticSuccess,
        deleteFailed: defaultActions.removeOnePessimisticFailed,
        fetchAssignedLocations: (state, payload: { offerId: ID; locationIds: ID[] }) =>
          adapter.markAsPending(state, {
            scope: null,
            entities: payload.locationIds,
          }),
        fetchAssignedLocationsSuccess: (
          state,
          payload: { locations: Location[]; locationIds: ID[]; offerId: ID },
        ) =>
          pipeState(
            state,
            next => adapter.updateMany(next, payload.locations),
            next =>
              adapter.markAsLoaded(next, {
                scope: null,
                entities: payload.locationIds,
              }),
          ),
        fetchAssignedLocationsFailed: (
          state,
          payload: { error: HttpErrorResponse; data: { locationIds: ID[]; offerId: ID } },
        ) =>
          adapter.setError(state, payload.error, {
            scope: null,
            entities: payload.data.locationIds,
          }),
      };
    },
  },
);

const byIds = memoizeSelector(
  (locationIds: number[]): MemoizedSelector<any, Location[]> =>
    createSelectorFactory(defaultMemoize)(
      ...(locationIds || []).map(locationId => selectors.byId(locationId)),
      (...result: Location[]) => result,
    ),
);

const LocationSelectors = { ...selectors, byIds };

export {
  actions as LocationActions,
  LocationSelectors,
  reducer as LocationReducer,
  namespace as LocationNamespace,
};
